import React, { useCallback, useState } from 'react';
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  IconButtonProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { styled, Theme, useTheme } from '@mui/material/styles';
import { AppRow } from '../../layout/appRow';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AppCardContent } from './appCardContent';
import { AppButtonProps } from './button/appButton';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  // eslint-disable-next-line no-unused-vars
  const { expand, ...other } = props;

  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

interface Props {
  title: string;
  statusInfo?: string | boolean;
  subTitle?: string;
  details: (string | JSX.Element)[];
  buttons: AppButtonProps[];
}

const makeStyles = (theme: Theme) => ({
  card: {
    width: 425,
    m: 1,
    mb: 1,
    pb: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },

  cardHeader: {
    [theme.breakpoints.down('sm')]: {
      padding: '0.5rem',
    },
  },

  detail: {
    paddingBottom: '5px',
    display: 'block',
  },
});

export function AppCard({
  title,
  statusInfo,
  details,
  subTitle,
  buttons,
}: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const handleExpand = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const theme = useTheme();
  const style = makeStyles(theme);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card
      sx={{
        ...style.card,
        background: statusInfo ? '#FFFFFF' : '#F3F3F3',
      }}
    >
      <CardHeader
        sx={style.cardHeader}
        title={
          <AppRow>
            <Typography variant="h4">{title}</Typography>
            <AppRow>
              {typeof statusInfo == 'string' && (
                <Typography variant="h5" marginRight={1}>
                  {statusInfo}
                </Typography>
              )}
              {typeof statusInfo == 'boolean' && (
                <CircleIcon
                  sx={{ color: statusInfo ? '#40A040' : '#9A9A9A' }}
                />
              )}

              {isMobile && (
                <ExpandMore
                  expand={expanded}
                  onClick={handleExpand}
                  aria-expanded={expanded}
                  aria-label="show more"
                >
                  <ExpandMoreIcon />
                </ExpandMore>
              )}
            </AppRow>
          </AppRow>
        }
      />
      {isMobile && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <AppCardContent
            subTitle={subTitle || ''}
            buttons={buttons}
            details={details}
          />
        </Collapse>
      )}
      {!isMobile && (
        <AppCardContent
          subTitle={subTitle || ''}
          buttons={buttons}
          details={details}
        />
      )}
    </Card>
  );
}
